import { useEffect, useRef } from 'react';
import { subscribeRealtime } from '../lib/realtime';

// Instant sync: WebSocket push (<1s) + interval poll fallback (only while tab
// is visible) + instant refetch when the user returns to the tab/window.
// `match`: optional array of path substrings — refresh only when a mutation
// hits a matching API path (omit to refresh on any mutation).
export default function useAutoRefresh(callback, { intervalMs = 25000, enabled = true, match } = {}) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const matchRef = useRef(match);
  matchRef.current = match;

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') cbRef.current();
    };
    const id = setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') cbRef.current();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    let debounceId = null;
    const unsubscribe = subscribeRealtime((msg) => {
      if (!msg || msg.type !== 'sync') return;
      const m = matchRef.current;
      if (m && m.length && !m.some((t) => (msg.path || '').includes(t))) return;
      if (document.visibilityState !== 'visible') return;
      clearTimeout(debounceId);
      debounceId = setTimeout(() => cbRef.current(), 350);
    });
    return () => {
      clearInterval(id);
      clearTimeout(debounceId);
      unsubscribe();
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs, enabled]);
}
